import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import clsx from 'clsx';
import { Publisher } from '../../../api/relevant';
import { readFile } from './utils';

const CsvUploader = forwardRef(({ onUploadFile, getReplaceModes, className }, ref) => {
	const [fileIsDraggedOver, setFileIsDraggedOver] = useState(false);
	const filePickerEl = useRef(null);
	const lastFile = useRef(null);

	const processFile = async (file) => {
		try {
			const csv = await readFile(file);
			const res = await Publisher.call('validateCsvImport', { csv, replaceMode: getReplaceModes() });
			lastFile.current = file;
			onUploadFile(res, csv);
		} catch (err) {
			onUploadFile(null, null, err.error);
		}
	};

	useImperativeHandle(ref, () => ({
		revalidateCsv: async () => {
			if (lastFile.current) {
				await processFile(lastFile.current);
			}
		},
	}));

	return (
		<>
			<Box
				component="input"
				ref={filePickerEl}
				type="file"
				accept="text/csv"
				sx={{ display: 'none' }}
				onChange={(e) => {
					const file = e.target.files[0];
					if (file) processFile(file);
				}}
			/>

			<div
				className={clsx(
					'flex flex-col items-center gap-1 border rounded-md py-6 cursor-pointer',
					fileIsDraggedOver ? 'border-cherry-400 border-solid' : 'border-grey-150 border-dashed',
					className,
				)}
				role="button"
				onClick={() => filePickerEl.current.click()}
				onDrop={(e) => {
					e.preventDefault();
					const file = e.dataTransfer.items?.[0]?.getAsFile();
					if (file) processFile(file);
				}}
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault(); // Prevent scrolling on spacebar
						filePickerEl.current.click();
					}
				}}
				onDragOver={(e) => {
					setFileIsDraggedOver(true);
					e.preventDefault();
				}}
				onDragLeave={() => setFileIsDraggedOver(false)}
			>
				<div className="bg-cherry-400 rounded-full p-4">
					<UploadFile className="text-white text-4xl" />
				</div>
				<button className="text-lg mt-4 no-underline">Select a CSV to import</button>
				<span className="text-sm">or drop it here</span>
			</div>
		</>
	);
});

CsvUploader.propTypes = {
	onUploadFile: PropTypes.func.isRequired,
	getReplaceModes: PropTypes.func.isRequired,
	className: PropTypes.string,
};

CsvUploader.defaultProps = {
	className: '',
};
export default CsvUploader;
